import { useState, useMemo, useCallback, memo, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DoorOpen, LayoutGrid, Calculator, Trash2, Plus, Copy, Info, Package, Printer, Save, FolderOpen, FileText, ChevronDown, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ CALCULADORA DE ALIZARES ============
const AlizarCalculator = memo(function AlizarCalculator() {
  const [items, setItems] = useState([]);
  const [larguraAlizar, setLarguraAlizar] = useState('7'); // cm
  const [precoMetro, setPrecoMetro] = useState('');
  
  const [novoItem, setNovoItem] = useState({
    tipo: 'porta', // 'porta' ou 'janela'
    largura: '',   // cm
    altura: '',    // cm
    quantidade: '',
    porDentro: true,
    porFora: false
  });

  // Cálculo para PORTA:
  // - 2 peças verticais: (altura + largura_alizar + 2cm) cada
  // - 1 peça horizontal (topo): (largura + 2*largura_alizar + 2cm)
  // Se por dentro E por fora: multiplica por 2
  
  // Cálculo para JANELA:
  // - 2 peças verticais: (altura + 2*largura_alizar + 2cm) cada
  // - 2 peças horizontais: (largura + 2*largura_alizar + 2cm) cada
  // Se por dentro E por fora: multiplica por 2

  const calcularMetragemItem = useCallback((item) => {
    const largAlizar = parseFloat(larguraAlizar) || 0;
    const largura = parseFloat(item.largura) || 0;
    const altura = parseFloat(item.altura) || 0;
    const qtd = parseInt(item.quantidade) || 1;
    
    let metragemUnitaria = 0;
    let pecas = [];
    
    if (item.tipo === 'porta') {
      // Verticais: 2x (altura + largura_alizar + 2cm)
      const tamanhoVertical = altura + largAlizar + 2;
      const vertical = tamanhoVertical * 2;
      // Horizontal (topo): largura + 2*largura_alizar
      const tamanhoHorizontal = largura + (2 * largAlizar);
      const horizontal = tamanhoHorizontal;
      metragemUnitaria = vertical + horizontal;
      
      pecas = [
        { tipo: 'Vertical (laterais)', tamanho: tamanhoVertical, quantidade: 2 },
        { tipo: 'Horizontal (topo)', tamanho: tamanhoHorizontal, quantidade: 1 }
      ];
    } else {
      // JANELA
      // Verticais: 2x (altura + 2*largura_alizar)
      const tamanhoVertical = altura + (2 * largAlizar);
      const vertical = tamanhoVertical * 2;
      // Horizontais: 2x (largura + 2*largura_alizar)
      const tamanhoHorizontal = largura + (2 * largAlizar);
      const horizontal = tamanhoHorizontal * 2;
      metragemUnitaria = vertical + horizontal;
      
      pecas = [
        { tipo: 'Vertical (laterais)', tamanho: tamanhoVertical, quantidade: 2 },
        { tipo: 'Horizontal (topo/base)', tamanho: tamanhoHorizontal, quantidade: 2 }
      ];
    }
    
    // Converter de cm para metros
    metragemUnitaria = metragemUnitaria / 100;
    
    // Multiplicar pela quantidade de portas/janelas
    let metragemTotal = metragemUnitaria * qtd;
    
    // Se for por dentro E por fora, dobra
    const lados = (item.porDentro ? 1 : 0) + (item.porFora ? 1 : 0);
    metragemTotal = metragemTotal * (lados || 1);
    
    // Ajustar quantidade de peças conforme quantidade e lados
    pecas = pecas.map(p => ({
      ...p,
      quantidadeTotal: p.quantidade * qtd * (lados || 1)
    }));
    
    return {
      metragemUnitaria,
      metragemTotal,
      lados,
      pecas
    };
  }, [larguraAlizar]);

  const handleAddItem = useCallback(() => {
    if (!novoItem.largura || !novoItem.altura) {
      toast.error('Informe largura e altura');
      return;
    }
    if (!novoItem.porDentro && !novoItem.porFora) {
      toast.error('Selecione pelo menos um lado (dentro ou fora)');
      return;
    }
    
    setItems(prev => [...prev, { ...novoItem, id: Date.now() }]);
    setNovoItem({
      tipo: 'porta',
      largura: '',
      altura: '',
      quantidade: '',
      porDentro: true,
      porFora: false
    });
    toast.success('Item adicionado!');
  }, [novoItem]);

  const handleRemoveItem = useCallback((id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const totais = useMemo(() => {
    let metragemTotal = 0;
    let totalPecas = 0;
    const resumoPecas = {}; // Agrupar por tamanho
    
    items.forEach(item => {
      const calc = calcularMetragemItem(item);
      metragemTotal += calc.metragemTotal;
      
      // Agregar peças por tamanho
      calc.pecas.forEach(peca => {
        const key = `${peca.tamanho.toFixed(1)}cm`;
        if (!resumoPecas[key]) {
          resumoPecas[key] = { tamanho: peca.tamanho, quantidade: 0 };
        }
        resumoPecas[key].quantidade += peca.quantidadeTotal;
        totalPecas += peca.quantidadeTotal;
      });
    });
    
    const preco = parseFloat(precoMetro) || 0;
    const valorTotal = metragemTotal * preco;
    
    // Converter resumoPecas para array ordenado por tamanho
    const pecasArray = Object.values(resumoPecas).sort((a, b) => b.tamanho - a.tamanho);
    
    return { metragemTotal, valorTotal, totalPecas, pecasArray };
  }, [items, precoMetro, calcularMetragemItem]);

  const copiarResultado = useCallback(() => {
    let pecasTexto = '';
    if (totais.pecasArray.length > 0) {
      pecasTexto = '\n\nPEÇAS:\n' + totais.pecasArray.map(p => 
        `• ${p.quantidade}x de ${p.tamanho.toFixed(1)}cm (${(p.tamanho/100).toFixed(2)}m)`
      ).join('\n');
      pecasTexto += `\nTotal: ${totais.totalPecas} peças`;
    }
    
    const texto = `ALIZARES - Largura: ${larguraAlizar}cm
Total: ${totais.metragemTotal.toFixed(2)}m lineares
${precoMetro ? `Valor: R$ ${totais.valorTotal.toFixed(2)}` : ''}${pecasTexto}`;
    navigator.clipboard.writeText(texto);
    toast.success('Resultado copiado!');
  }, [larguraAlizar, totais, precoMetro]);

  return (
    <div className="space-y-4">
      {/* Configuração Global */}
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <DoorOpen className="h-5 w-5 text-primary" />
            Configuração do Alizar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Largura do Alizar (cm)</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="Ex: 7"
                value={larguraAlizar}
                onChange={(e) => setLarguraAlizar(e.target.value)}
              />
            </div>
            <div>
              <Label>Preço por Metro Linear (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 25.00"
                value={precoMetro}
                onChange={(e) => setPrecoMetro(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Adicionar Item */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Adicionar Porta/Janela</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={novoItem.tipo} onValueChange={(v) => setNovoItem({ ...novoItem, tipo: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="porta">Porta</SelectItem>
                  <SelectItem value="janela">Janela</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Largura (cm)</Label>
              <Input
                type="number"
                placeholder="Ex: 80"
                value={novoItem.largura}
                onChange={(e) => setNovoItem({ ...novoItem, largura: e.target.value })}
              />
            </div>
            <div>
              <Label>Altura (cm)</Label>
              <Input
                type="number"
                placeholder="Ex: 210"
                value={novoItem.altura}
                onChange={(e) => setNovoItem({ ...novoItem, altura: e.target.value })}
              />
            </div>
            <div>
              <Label>Quantidade</Label>
              <Input
                type="number"
                min="1"
                placeholder="1"
                value={novoItem.quantidade}
                onChange={(e) => setNovoItem({ ...novoItem, quantidade: e.target.value })}
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id="porDentro"
                checked={novoItem.porDentro}
                onCheckedChange={(checked) => setNovoItem({ ...novoItem, porDentro: checked })}
              />
              <Label htmlFor="porDentro" className="cursor-pointer">Por Dentro</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="porFora"
                checked={novoItem.porFora}
                onCheckedChange={(checked) => setNovoItem({ ...novoItem, porFora: checked })}
              />
              <Label htmlFor="porFora" className="cursor-pointer">Por Fora</Label>
            </div>
          </div>

          <Button onClick={handleAddItem} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar
          </Button>
        </CardContent>
      </Card>

      {/* Lista de Itens */}
      {items.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Itens Adicionados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {items.map((item) => {
                const calc = calcularMetragemItem(item);
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={item.tipo === 'porta' ? 'default' : 'secondary'}>
                          {item.tipo === 'porta' ? 'Porta' : 'Janela'}
                        </Badge>
                        <span className="text-sm font-medium">
                          {item.largura}x{item.altura}cm
                        </span>
                        <span className="text-sm text-muted-foreground">
                          x{item.quantidade}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({item.porDentro && 'Dentro'}{item.porDentro && item.porFora && ' + '}{item.porFora && 'Fora'})
                        </span>
                      </div>
                      <div className="text-sm text-primary font-semibold mt-1">
                        {calc.metragemTotal.toFixed(2)}m lineares
                        {precoMetro && (
                          <span className="ml-2 text-muted-foreground">
                            = R$ {(calc.metragemTotal * parseFloat(precoMetro)).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo de Peças */}
      {totais.pecasArray.length > 0 && (
        <Card className="border-orange-500/30 bg-orange-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5 text-orange-500" />
              Resumo de Peças
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {totais.pecasArray.map((peca, idx) => (
                <li key={idx} className="flex items-center justify-between p-2 bg-background rounded-lg border">
                  <span className="text-sm">
                    <span className="font-bold text-orange-500">{peca.quantidade}x</span>
                    {' '}peças de{' '}
                    <span className="font-semibold">{peca.tamanho.toFixed(1)}cm</span>
                    <span className="text-muted-foreground ml-1">({(peca.tamanho/100).toFixed(2)}m)</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total de peças:</span>
              <span className="text-xl font-bold">{totais.totalPecas} peças</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado Final */}
      <Card className="border-green-500/30 bg-green-500/5">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total de Metragem Linear</p>
              <p className="text-3xl font-bold text-green-500">
                {totais.metragemTotal.toFixed(2)}m
              </p>
              {precoMetro && (
                <p className="text-lg font-semibold mt-1">
                  Valor Total: R$ {totais.valorTotal.toFixed(2)}
                </p>
              )}
            </div>
            <Button variant="outline" onClick={copiarResultado} disabled={items.length === 0}>
              <Copy className="h-4 w-4 mr-2" />
              Copiar Resultado
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dica */}
      <div className="flex items-start gap-2 text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Fórmula utilizada:</strong><br />
          <span className="text-xs">
            • Porta: 2×(altura + largura_alizar + 2cm) + (largura + 2×largura_alizar)<br />
            • Janela: 2×(altura + 2×largura_alizar) + 2×(largura + 2×largura_alizar)
          </span>
        </div>
      </div>
    </div>
  );
});

// ============ CALCULADORA DE FORRO DE LAMBRI ============
const LambriCalculator = memo(function LambriCalculator() {
  const [ambientes, setAmbientes] = useState([]);
  const [precoM2, setPrecoM2] = useState('');
  const [precoRodaTeto, setPrecoRodaTeto] = useState('');
  const [comDesperdicio, setComDesperdicio] = useState(false); // 10% de desperdício — opt-in (usuário marca quando quer)

  // Cliente e opções gerais do orçamento
  const [clienteId, setClienteId] = useState('');
  const [clienteNome, setClienteNome] = useState('');
  const [clienteTelefone, setClienteTelefone] = useState('');
  const [clienteBusca, setClienteBusca] = useState('');
  const [clienteResults, setClienteResults] = useState([]);
  const [showNovoClienteModal, setShowNovoClienteModal] = useState(false);
  const [novoClienteForm, setNovoClienteForm] = useState({ nome: '', telefone: '', cpf_cnpj: '', endereco: '', bairro: '', cidade: '', estado: 'PB', cep: '', tipo: 'PF' });
  const [savingCliente, setSavingCliente] = useState(false);

  const [observacoesGerais, setObservacoesGerais] = useState('');
  const [exportando, setExportando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [orcamentoAtualId, setOrcamentoAtualId] = useState(null);
  const [orcamentosSalvos, setOrcamentosSalvos] = useState([]);
  const [mostrarSalvos, setMostrarSalvos] = useState(false);
  const [livreCompAtual, setLivreCompAtual] = useState('');

  const [novoAmbiente, setNovoAmbiente] = useState({
    nome: '',
    comprimento: '',
    largura: '',
    comRodaTeto: false,
    direcaoLambri: 'comprimento', // comprimento | largura
    temEmenda: 'nao', // nao | sim
    formato: 'retangular', // retangular | l | u | sacada | livre
    recuoComprimento: '',
    recuoLargura: '',
    cantoRecuo: 'SUP-DIR',
    parede: 'TOP',
    offsetParede: '',
    segmentos: [], // [{direcao: 'N'|'S'|'E'|'O', comprimento: number}]
  });

  const buscarCliente = useCallback(async () => {
    const termo = clienteBusca.trim();
    if (termo.length < 2) { setClienteResults([]); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/customers?search=${encodeURIComponent(termo)}&limit=8`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      const list = Array.isArray(data) ? data : (data.customers || data.data || []);
      setClienteResults(list.slice(0, 8));
    } catch (e) {
      setClienteResults([]);
    }
  }, [clienteBusca]);

  const selecionarCliente = (c) => {
    setClienteId(c.id);
    setClienteNome(c.nome || '');
    setClienteTelefone(c.telefone || '');
    setClienteBusca('');
    setClienteResults([]);
  };

  const cadastrarNovoCliente = async () => {
    if (!novoClienteForm.nome?.trim()) { toast.error('Informe o nome'); return; }
    setSavingCliente(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/customers`, novoClienteForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cliente cadastrado!');
      selecionarCliente(res.data);
      setShowNovoClienteModal(false);
      setNovoClienteForm({ nome: '', telefone: '', cpf_cnpj: '', endereco: '', bairro: '', cidade: '', estado: 'PB', cep: '', tipo: 'PF' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cadastrar');
    } finally { setSavingCliente(false); }
  };

  // Calcular dados de um ambiente (considera formato retangular, L, U e Sacada)
  const calcularAmbiente = useCallback((ambiente) => {
    const comp = parseFloat(ambiente.comprimento) || 0;
    const larg = parseFloat(ambiente.largura) || 0;
    const multiplicador = comDesperdicio ? 1.10 : 1;

    const formato = ambiente.formato || 'retangular';
    const recC = parseFloat(ambiente.recuoComprimento) || 0;
    const recL = parseFloat(ambiente.recuoLargura) || 0;
    const parede = ambiente.parede || 'TOP';
    const offP = parseFloat(ambiente.offsetParede) || 0;

    const isL = formato === 'l' && recC > 0 && recL > 0 && recC < comp && recL < larg;
    let isU = false;
    if (formato === 'u' && recC > 0 && recL > 0) {
      if (parede === 'TOP' || parede === 'BOTTOM') {
        isU = offP >= 0 && recC < comp && (offP + recC) <= comp && recL < larg;
      } else {
        isU = offP >= 0 && recC < larg && (offP + recC) <= larg && recL < comp;
      }
    }
    let isSacada = false;
    if (formato === 'sacada' && recC > 0 && recL > 0) {
      if (parede === 'TOP' || parede === 'BOTTOM') {
        isSacada = offP >= 0 && (offP + recC) <= comp;
      } else {
        isSacada = offP >= 0 && (offP + recC) <= larg;
      }
    }
    const segs = ambiente.segmentos || [];
    const isLivre = formato === 'livre' && segs.length >= 3;

    // Área
    let areaBruta;
    let bboxW = 0, bboxH = 0;
    if (isLivre) {
      // Traça polígono a partir dos segmentos e calcula área via shoelace
      let x = 0, y = 0, minX = 0, maxX = 0, minY = 0, maxY = 0;
      const ptsM = [[0, 0]];
      for (const s of segs) {
        const c = parseFloat(s.comprimento) || 0;
        if (s.direcao === 'N') y += c;
        else if (s.direcao === 'S') y -= c;
        else if (s.direcao === 'E') x += c;
        else if (s.direcao === 'O') x -= c;
        ptsM.push([x, y]);
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
      // Remove duplicado se fechou
      if (ptsM.length > 1 && Math.abs(ptsM[ptsM.length - 1][0] - ptsM[0][0]) < 1e-6 && Math.abs(ptsM[ptsM.length - 1][1] - ptsM[0][1]) < 1e-6) {
        ptsM.pop();
      }
      let s2 = 0;
      for (let i = 0; i < ptsM.length; i++) {
        const [x1, y1] = ptsM[i];
        const [x2, y2] = ptsM[(i + 1) % ptsM.length];
        s2 += (x1 * y2 - x2 * y1);
      }
      areaBruta = Math.abs(s2) / 2;
      bboxW = maxX - minX;
      bboxH = maxY - minY;
    } else if (isL || isU) areaBruta = comp * larg - recC * recL;
    else if (isSacada) areaBruta = comp * larg + recC * recL;
    else areaBruta = comp * larg;
    const areaFinal = areaBruta * multiplicador;

    const direcao = ambiente.direcaoLambri || 'comprimento';
    const reguas = [];
    const push = (metros, compRegua) => {
      if (metros <= 0 || compRegua <= 0) return;
      reguas.push({ quantidade: Math.ceil(metros * 11 * multiplicador), comprimento: compRegua });
    };

    if (isLivre) {
      // Aproximação: total_length_régua = area × 11 mˡ/m² × mult; num = total / L
      const L = direcao === 'comprimento' ? bboxW : bboxH;
      if (L > 0) {
        const totalL = areaBruta * 11 * multiplicador;
        reguas.push({ quantidade: Math.ceil(totalL / L), comprimento: L });
      }
    } else if (isL) {
      if (direcao === 'largura') { push(comp - recC, larg); push(recC, larg - recL); }
      else { push(larg - recL, comp); push(recL, comp - recC); }
    } else if (isU) {
      if (direcao === 'comprimento') {
        if (parede === 'TOP' || parede === 'BOTTOM') {
          push(larg - recL, comp); push(recL, offP); push(recL, comp - offP - recC);
        } else {
          push(offP, comp); push(recC, comp - recL); push(larg - offP - recC, comp);
        }
      } else {
        if (parede === 'LEFT' || parede === 'RIGHT') {
          push(comp - recL, larg); push(recL, larg - recC);
        } else {
          push(offP, larg); push(recC, larg - recL); push(comp - offP - recC, larg);
        }
      }
    } else if (isSacada) {
      if (direcao === 'comprimento') {
        if (parede === 'TOP' || parede === 'BOTTOM') { push(larg, comp); push(recL, recC); }
        else { push(larg - recC, comp); push(recC, comp + recL); }
      } else {
        if (parede === 'LEFT' || parede === 'RIGHT') { push(comp, larg); push(recL, recC); }
        else { push(comp - recC, larg); push(recC, larg + recL); }
      }
    } else if (direcao === 'largura') {
      push(comp, larg);
    } else {
      push(larg, comp);
    }

    const primeira = reguas[0] || { quantidade: 0, comprimento: 0 };
    const quantidadeReguas = reguas.reduce((s, r) => s + r.quantidade, 0);

    // Roda-teto: perímetro externo (sacada estende parede em 2*recL; livre = soma dos segmentos)
    let perimetro = 0;
    if (ambiente.comRodaTeto) {
      if (isLivre) {
        perimetro = segs.reduce((s, seg) => s + (parseFloat(seg.comprimento) || 0), 0);
      } else {
        perimetro = 2 * comp + 2 * larg + (isSacada ? 2 * recL : 0);
      }
    }

    return {
      areaBruta,
      areaFinal,
      quantidadeReguas,
      comprimentoRegua: primeira.comprimento,
      reguas,
      perimetro,
      isL,
      isU,
      isSacada,
      isLivre,
    };
  }, [comDesperdicio]);

  const handleAddAmbiente = useCallback(() => {
    // No Formato Livre não é preciso comp/larg — as medidas vêm dos segmentos (bbox automático)
    if (novoAmbiente.formato !== 'livre') {
      if (!novoAmbiente.comprimento || !novoAmbiente.largura) {
        toast.error('Informe comprimento e largura');
        return;
      }
    }
    const comp = parseFloat(novoAmbiente.comprimento);
    const larg = parseFloat(novoAmbiente.largura);
    const rc = parseFloat(novoAmbiente.recuoComprimento);
    const rl_ = parseFloat(novoAmbiente.recuoLargura);
    const off = parseFloat(novoAmbiente.offsetParede);

    if (novoAmbiente.formato === 'l') {
      if (!rc || !rl_ || rc <= 0 || rl_ <= 0 || rc >= comp || rl_ >= larg) {
        toast.error('Recuo em L: informe valores > 0 e menores que o total');
        return;
      }
    }
    if (novoAmbiente.formato === 'u') {
      if (!rc || !rl_ || rc <= 0 || rl_ <= 0) {
        toast.error('Recuo em U: informe comprimento e profundidade > 0');
        return;
      }
      const eixoLimite = (novoAmbiente.parede === 'TOP' || novoAmbiente.parede === 'BOTTOM') ? comp : larg;
      const eixoProf = (novoAmbiente.parede === 'TOP' || novoAmbiente.parede === 'BOTTOM') ? larg : comp;
      if ((off || 0) < 0 || (off || 0) + rc > eixoLimite || rl_ >= eixoProf) {
        toast.error('U: recuo + offset deve caber na parede e profundidade menor que o outro eixo');
        return;
      }
    }
    if (novoAmbiente.formato === 'sacada') {
      if (!rc || !rl_ || rc <= 0 || rl_ <= 0) {
        toast.error('Sacada: informe comprimento e profundidade > 0');
        return;
      }
      const eixoLimite = (novoAmbiente.parede === 'TOP' || novoAmbiente.parede === 'BOTTOM') ? comp : larg;
      if ((off || 0) < 0 || (off || 0) + rc > eixoLimite) {
        toast.error('Sacada: offset + comprimento não pode ultrapassar a parede');
        return;
      }
    }
    if (novoAmbiente.formato === 'livre') {
      const segs = novoAmbiente.segmentos || [];
      if (segs.length < 3) {
        toast.error('Livre: adicione ao menos 3 segmentos');
        return;
      }
      // Verifica fechamento (soma vetorial ≈ 0)
      let sx = 0, sy = 0;
      for (const s of segs) {
        const c = parseFloat(s.comprimento) || 0;
        if (c <= 0) { toast.error('Livre: todos os segmentos precisam ter comprimento > 0'); return; }
        if (s.direcao === 'N') sy += c;
        else if (s.direcao === 'S') sy -= c;
        else if (s.direcao === 'E') sx += c;
        else if (s.direcao === 'O') sx -= c;
      }
      if (Math.abs(sx) > 0.01 || Math.abs(sy) > 0.01) {
        toast.error(`Livre: polígono não fecha (desvio ${sx.toFixed(2)}m X / ${sy.toFixed(2)}m Y)`);
        return;
      }
    }

    const nome = novoAmbiente.nome || `Ambiente ${ambientes.length + 1}`;
    // Formato Livre: auto-popula comprimento/largura a partir do bbox dos segmentos
    let ambienteFinal = { ...novoAmbiente, nome, id: Date.now() };
    if (novoAmbiente.formato === 'livre') {
      const segs = novoAmbiente.segmentos || [];
      let x = 0, y = 0, minX = 0, maxX = 0, minY = 0, maxY = 0;
      for (const s of segs) {
        const c = parseFloat(s.comprimento) || 0;
        if (s.direcao === 'N') y += c;
        else if (s.direcao === 'S') y -= c;
        else if (s.direcao === 'E') x += c;
        else if (s.direcao === 'O') x -= c;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
      ambienteFinal = {
        ...ambienteFinal,
        comprimento: String((maxX - minX).toFixed(2)),
        largura: String((maxY - minY).toFixed(2)),
      };
    }
    setAmbientes(prev => [...prev, ambienteFinal]);
    setNovoAmbiente({
      nome: '',
      comprimento: '',
      largura: '',
      comRodaTeto: false,
      direcaoLambri: 'comprimento',
      temEmenda: 'nao',
      formato: 'retangular',
      recuoComprimento: '',
      recuoLargura: '',
      cantoRecuo: 'SUP-DIR',
      parede: 'TOP',
      offsetParede: '',
      segmentos: [],
    });
    toast.success('Ambiente adicionado!');
  }, [novoAmbiente, ambientes.length]);

  // Retorna os pontos (em metros, y-up) do polígono do ambiente conforme seu formato.
  // Usado por mini-preview e desenho ao vivo.
  const getPolygonPointsForAmbiente = useCallback((ambiente) => {
    const comp = parseFloat(ambiente.comprimento) || 0;
    const larg = parseFloat(ambiente.largura) || 0;
    const rc = parseFloat(ambiente.recuoComprimento) || 0;
    const rl = parseFloat(ambiente.recuoLargura) || 0;
    const off = parseFloat(ambiente.offsetParede) || 0;
    const formato = ambiente.formato || 'retangular';
    const canto = ambiente.cantoRecuo || 'SUP-DIR';
    const parede = ambiente.parede || 'TOP';
    if (formato === 'livre') {
      const segs = ambiente.segmentos || [];
      let x = 0, y = 0;
      const pts = [[0, 0]];
      for (const s of segs) {
        const c = parseFloat(s.comprimento) || 0;
        if (s.direcao === 'N') y += c;
        else if (s.direcao === 'S') y -= c;
        else if (s.direcao === 'E') x += c;
        else if (s.direcao === 'O') x -= c;
        pts.push([x, y]);
      }
      if (pts.length > 1 && Math.abs(pts[pts.length - 1][0] - pts[0][0]) < 1e-6 && Math.abs(pts[pts.length - 1][1] - pts[0][1]) < 1e-6) pts.pop();
      return pts;
    }
    if (formato === 'l' && rc > 0 && rl > 0 && rc < comp && rl < larg) {
      if (canto === 'SUP-DIR') return [[0, 0], [comp, 0], [comp, larg - rl], [comp - rc, larg - rl], [comp - rc, larg], [0, larg]];
      if (canto === 'SUP-ESQ') return [[0, 0], [comp, 0], [comp, larg], [rc, larg], [rc, larg - rl], [0, larg - rl]];
      if (canto === 'INF-DIR') return [[0, 0], [comp - rc, 0], [comp - rc, rl], [comp, rl], [comp, larg], [0, larg]];
      return [[rc, 0], [comp, 0], [comp, larg], [0, larg], [0, rl], [rc, rl]];
    }
    if (formato === 'u' && rc > 0 && rl > 0) {
      if (parede === 'TOP') return [[0, 0], [comp, 0], [comp, larg], [off + rc, larg], [off + rc, larg - rl], [off, larg - rl], [off, larg], [0, larg]];
      if (parede === 'BOTTOM') return [[0, 0], [off, 0], [off, rl], [off + rc, rl], [off + rc, 0], [comp, 0], [comp, larg], [0, larg]];
      if (parede === 'LEFT') return [[0, 0], [comp, 0], [comp, larg], [0, larg], [0, larg - off], [rl, larg - off], [rl, larg - off - rc], [0, larg - off - rc]];
      return [[0, 0], [comp, 0], [comp, larg - off - rc], [comp - rl, larg - off - rc], [comp - rl, larg - off], [comp, larg - off], [comp, larg], [0, larg]];
    }
    if (formato === 'sacada' && rc > 0 && rl > 0) {
      if (parede === 'TOP') return [[0, 0], [comp, 0], [comp, larg], [off + rc, larg], [off + rc, larg + rl], [off, larg + rl], [off, larg], [0, larg]];
      if (parede === 'BOTTOM') return [[0, 0], [off, 0], [off, -rl], [off + rc, -rl], [off + rc, 0], [comp, 0], [comp, larg], [0, larg]];
      if (parede === 'LEFT') return [[0, 0], [comp, 0], [comp, larg], [0, larg], [0, larg - off], [-rl, larg - off], [-rl, larg - off - rc], [0, larg - off - rc]];
      return [[0, 0], [comp, 0], [comp, larg - off - rc], [comp + rl, larg - off - rc], [comp + rl, larg - off], [comp, larg - off], [comp, larg], [0, larg]];
    }
    // retangular
    return [[0, 0], [comp, 0], [comp, larg], [0, larg]];
  }, []);

  const handleRemoveAmbiente = useCallback((id) => {
    setAmbientes(prev => prev.filter(a => a.id !== id));
  }, []);

  const handleEditAmbiente = useCallback((ambiente) => {
    // Carrega os dados do ambiente para o formulário e remove da lista;
    // ao clicar "Adicionar", ele é reinserido atualizado.
    setNovoAmbiente({
      nome: ambiente.nome || '',
      comprimento: String(ambiente.comprimento ?? ''),
      largura: String(ambiente.largura ?? ''),
      comRodaTeto: !!ambiente.comRodaTeto,
      direcaoLambri: ambiente.direcaoLambri || 'comprimento',
      temEmenda: ambiente.temEmenda || 'nao',
      formato: ambiente.formato || 'retangular',
      recuoComprimento: ambiente.recuoComprimento ? String(ambiente.recuoComprimento) : '',
      recuoLargura: ambiente.recuoLargura ? String(ambiente.recuoLargura) : '',
      cantoRecuo: ambiente.cantoRecuo || 'SUP-DIR',
      parede: ambiente.parede || 'TOP',
      offsetParede: ambiente.offsetParede ? String(ambiente.offsetParede) : '',
      segmentos: (ambiente.segmentos || []).map(s => ({ direcao: s.direcao, comprimento: parseFloat(s.comprimento) || 0 })),
    });
    setAmbientes(prev => prev.filter(a => a.id !== ambiente.id));
    // scroll ao topo do form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(`Ambiente "${ambiente.nome}" carregado para edição`);
  }, []);

  // Totais gerais
  const totais = useMemo(() => {
    let areaTotal = 0;
    let areaTotalFinal = 0;
    let perimetroTotal = 0;
    const resumoReguas = {}; // Agrupar por comprimento
    
    ambientes.forEach(ambiente => {
      const calc = calcularAmbiente(ambiente);
      areaTotal += calc.areaBruta;
      areaTotalFinal += calc.areaFinal;
      perimetroTotal += calc.perimetro;

      // Agrupar réguas por comprimento (cada faixa entra separada)
      (calc.reguas || []).forEach(r => {
        if (!r || r.quantidade <= 0 || r.comprimento <= 0) return;
        const key = `${r.comprimento.toFixed(2)}m`;
        if (!resumoReguas[key]) {
          resumoReguas[key] = { comprimento: r.comprimento, quantidade: 0 };
        }
        resumoReguas[key].quantidade += r.quantidade;
      });
    });
    
    const preco = parseFloat(precoM2) || 0;
    const precoRT = parseFloat(precoRodaTeto) || 0;
    const valorLambri = areaTotalFinal * preco;
    const valorRodaTeto = perimetroTotal * precoRT;
    const valorTotal = valorLambri + valorRodaTeto;
    
    // Converter para array ordenado do maior para menor
    const reguasArray = Object.values(resumoReguas).sort((a, b) => b.comprimento - a.comprimento);
    const totalReguas = reguasArray.reduce((sum, r) => sum + r.quantidade, 0);
    
    return {
      areaTotal,
      areaTotalFinal,
      perimetroTotal,
      valorLambri,
      valorRodaTeto,
      valorTotal,
      reguasArray,
      totalReguas
    };
  }, [ambientes, precoM2, precoRodaTeto, calcularAmbiente]);

  const copiarResultado = useCallback(() => {
    let reguasTexto = '';
    if (totais.reguasArray.length > 0) {
      reguasTexto = '\n\nRÉGUAS:\n' + totais.reguasArray.map(r => 
        `• ${r.quantidade}x de ${r.comprimento.toFixed(2)}m`
      ).join('\n');
      reguasTexto += `\nTotal: ${totais.totalReguas} réguas`;
    }
    
    let rodaTetoTexto = '';
    if (totais.perimetroTotal > 0) {
      rodaTetoTexto = `\n\nRODA-TETO: ${totais.perimetroTotal.toFixed(2)}m lineares`;
      if (precoRodaTeto) {
        rodaTetoTexto += ` = R$ ${totais.valorRodaTeto.toFixed(2)}`;
      }
    }
    
    const texto = `FORRO LAMBRI
Área: ${totais.areaTotal.toFixed(2)}m²${comDesperdicio ? `\nCom 10% desperdício: ${totais.areaTotalFinal.toFixed(2)}m²` : ''}
${precoM2 ? `Valor Lambri: R$ ${totais.valorLambri.toFixed(2)}` : ''}${rodaTetoTexto}${reguasTexto}
${precoM2 || precoRodaTeto ? `\nVALOR TOTAL: R$ ${totais.valorTotal.toFixed(2)}` : ''}`;
    navigator.clipboard.writeText(texto);
    toast.success('Resultado copiado!');
  }, [totais, precoM2, precoRodaTeto, comDesperdicio]);

  const carregarOrcamentosSalvos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/lambri-orcamentos`, { headers: { Authorization: `Bearer ${token}` } });
      setOrcamentosSalvos(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { carregarOrcamentosSalvos(); }, [carregarOrcamentosSalvos]);

  const construirPayload = useCallback(() => ({
    cliente_id: clienteId || null,
    cliente_nome: clienteNome || '',
    cliente_telefone: clienteTelefone || '',
    cliente_endereco: '',
    ambientes: ambientes.map((a) => ({
      nome: a.nome || '',
      comprimento: parseFloat(a.comprimento) || 0,
      largura: parseFloat(a.largura) || 0,
      comRodaTeto: !!a.comRodaTeto,
      direcaoLambri: a.direcaoLambri || 'comprimento',
      temEmenda: a.temEmenda || 'nao',
      formato: a.formato || 'retangular',
      recuoComprimento: parseFloat(a.recuoComprimento) || 0,
      recuoLargura: parseFloat(a.recuoLargura) || 0,
      cantoRecuo: a.cantoRecuo || 'SUP-DIR',
      parede: a.parede || 'TOP',
      offsetParede: parseFloat(a.offsetParede) || 0,
      segmentos: (a.segmentos || []).map((s) => ({ direcao: s.direcao || 'E', comprimento: parseFloat(s.comprimento) || 0 })),
    })),
    precoM2: parseFloat(precoM2) || 0,
    precoRodaTeto: parseFloat(precoRodaTeto) || 0,
    comDesperdicio: !!comDesperdicio,
    observacoes: observacoesGerais || '',
  }), [ambientes, comDesperdicio, precoM2, precoRodaTeto, clienteId, clienteNome, clienteTelefone, observacoesGerais]);

  const salvarOrcamento = useCallback(async () => {
    if (ambientes.length === 0) { toast.error('Adicione ao menos um ambiente'); return; }
    setSalvando(true);
    try {
      const token = localStorage.getItem('token');
      const payload = construirPayload();
      let res;
      if (orcamentoAtualId) {
        res = await axios.put(`${API}/lambri-orcamentos/${orcamentoAtualId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Orçamento atualizado!');
      } else {
        res = await axios.post(`${API}/lambri-orcamentos`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setOrcamentoAtualId(res.data.id);
        toast.success(`Orçamento salvo (${res.data.numero})`);
      }
      await carregarOrcamentosSalvos();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erro ao salvar orçamento');
    } finally {
      setSalvando(false);
    }
  }, [ambientes.length, construirPayload, orcamentoAtualId, carregarOrcamentosSalvos]);

  const novoFormulario = useCallback(() => {
    setOrcamentoAtualId(null);
    setAmbientes([]);
    setClienteId('');
    setClienteNome('');
    setClienteTelefone('');
    setObservacoesGerais('');
    setPrecoM2('');
    setPrecoRodaTeto('');
    toast.info('Novo orçamento — formulário limpo');
  }, []);

  const carregarOrcamentoParaEditar = useCallback((quote) => {
    setOrcamentoAtualId(quote.id);
    setClienteId(quote.cliente_id || '');
    setClienteNome(quote.cliente_nome || '');
    setClienteTelefone(quote.cliente_telefone || '');
    setObservacoesGerais(quote.observacoes || '');
    setPrecoM2(String(quote.precoM2 || ''));
    setPrecoRodaTeto(String(quote.precoRodaTeto || ''));
    setComDesperdicio(!!quote.comDesperdicio);
    setAmbientes((quote.ambientes || []).map((a, idx) => ({
      ...a,
      comprimento: String(a.comprimento ?? ''),
      largura: String(a.largura ?? ''),
      recuoComprimento: a.recuoComprimento ? String(a.recuoComprimento) : '',
      recuoLargura: a.recuoLargura ? String(a.recuoLargura) : '',
      offsetParede: a.offsetParede ? String(a.offsetParede) : '',
      id: Date.now() + idx,
    })));
    setMostrarSalvos(false);
    toast.info(`Orçamento ${quote.numero} carregado para edição`);
  }, []);

  const excluirOrcamentoSalvo = useCallback(async (quoteId) => {
    if (!window.confirm('Excluir este orçamento?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/lambri-orcamentos/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Orçamento excluído');
      if (orcamentoAtualId === quoteId) novoFormulario();
      await carregarOrcamentosSalvos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  }, [orcamentoAtualId, novoFormulario, carregarOrcamentosSalvos]);

  const converterOrcamentoEmPedido = useCallback(async (quoteId) => {
    if (!window.confirm('Converter este orçamento em Pedido? O orçamento não poderá mais ser editado.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/lambri-orcamentos/${quoteId}/convert-to-order`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const pedidoNumero = res.data?.order?.numero_pedido || res.data?.order?.id;
      toast.success(`Pedido criado: ${pedidoNumero}`);
      await carregarOrcamentosSalvos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao converter em pedido');
    }
  }, [carregarOrcamentosSalvos]);

  const baixarPdfSalvo = useCallback(async (quoteId, nome) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/lambri-orcamentos/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = url; a.download = `orcamento_lambri_${nome || quoteId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Erro ao baixar PDF');
    }
  }, []);



  const exportarOrcamento = useCallback(async () => {
    if (ambientes.length === 0) {
      toast.error('Adicione ao menos um ambiente');
      return;
    }
    setExportando(true);
    try {
      const token = localStorage.getItem('token');
      const payload = construirPayload();
      const res = await axios.post(`${API}/calculators/lambri/pdf`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Abrir em nova aba (usuário pode salvar/imprimir a partir do visualizador)
      const win = window.open(url, '_blank');
      if (!win) {
        // Fallback: forçar download
        const a = document.createElement('a');
        a.href = url;
        const cliSlug = (clienteNome || 'cliente').trim().replace(/\s+/g, '_');
        a.download = `orcamento_lambri_${cliSlug}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success('Orçamento gerado!');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erro ao gerar orçamento');
    } finally {
      setExportando(false);
    }
  }, [ambientes.length, construirPayload, clienteNome]);


  return (
    <div className="space-y-4">
      {/* Configuração de Preços */}
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <LayoutGrid className="h-5 w-5 text-primary" />
            Configuração
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cliente + opções do orçamento */}
          <div className="p-3 border rounded-lg space-y-3 bg-muted/20">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Cliente e Orçamento</div>
            {!clienteId ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="Buscar cliente por nome/telefone..." value={clienteBusca}
                    onChange={(e) => setClienteBusca(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), buscarCliente())}
                    data-testid="lambri-cliente-busca" />
                  <Button type="button" variant="outline" onClick={buscarCliente}>Buscar</Button>
                  <Button type="button" variant="outline" onClick={() => setShowNovoClienteModal(true)} data-testid="lambri-novo-cliente">
                    <Plus className="h-4 w-4 mr-1" />Novo
                  </Button>
                </div>
                {clienteResults.length > 0 && (
                  <div className="border rounded max-h-48 overflow-y-auto bg-background">
                    {clienteResults.map(c => (
                      <button key={c.id} type="button" onClick={() => selecionarCliente(c)}
                        className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-0">
                        <div className="font-medium">{c.nome}</div>
                        {c.telefone && <div className="text-xs text-muted-foreground">{c.telefone}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-2 bg-background border rounded">
                <div>
                  <div className="font-semibold text-sm">{clienteNome}</div>
                  {clienteTelefone && <div className="text-xs text-muted-foreground">{clienteTelefone}</div>}
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={() => { setClienteId(''); setClienteNome(''); setClienteTelefone(''); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            <div>
              <Label className="text-xs">Observações do orçamento</Label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[50px]"
                placeholder="Detalhes adicionais para o orçamento (opcional)..."
                value={observacoesGerais} onChange={(e) => setObservacoesGerais(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Preço Lambri por m² (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 85.00"
                value={precoM2}
                onChange={(e) => setPrecoM2(e.target.value)}
              />
            </div>
            <div>
              <Label>Preço Roda-teto por metro (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 15.00"
                value={precoRodaTeto}
                onChange={(e) => setPrecoRodaTeto(e.target.value)}
              />
            </div>
            <div className="flex items-end pb-1">
              <div className="flex items-center gap-2">
                <Switch
                  id="comDesperdicio"
                  checked={comDesperdicio}
                  onCheckedChange={setComDesperdicio}
                />
                <Label htmlFor="comDesperdicio" className="cursor-pointer text-sm">
                  Adicionar 10%
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Adicionar Ambiente */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Adicionar Ambiente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Nome (opcional)</Label>
              <Input
                placeholder="Ex: Sala"
                value={novoAmbiente.nome}
                onChange={(e) => setNovoAmbiente({ ...novoAmbiente, nome: e.target.value })}
              />
            </div>
            <div>
              <Label>Comprimento (m)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 5.00"
                value={novoAmbiente.comprimento}
                onChange={(e) => setNovoAmbiente({ ...novoAmbiente, comprimento: e.target.value })}
              />
            </div>
            <div>
              <Label>Largura (m)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 4.00"
                value={novoAmbiente.largura}
                onChange={(e) => setNovoAmbiente({ ...novoAmbiente, largura: e.target.value })}
              />
            </div>
            <div className="flex items-end pb-1">
              <div className="flex items-center gap-2">
                <Switch
                  id="comRodaTeto"
                  checked={novoAmbiente.comRodaTeto}
                  onCheckedChange={(checked) => setNovoAmbiente({ ...novoAmbiente, comRodaTeto: checked })}
                />
                <Label htmlFor="comRodaTeto" className="cursor-pointer text-sm">Roda-teto</Label>
              </div>
            </div>
          </div>

          {/* Direção do Lambri e Emenda (por-ambiente) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label className="text-xs">Direção das Réguas</Label>
              <Select value={novoAmbiente.direcaoLambri} onValueChange={(v) => setNovoAmbiente({ ...novoAmbiente, direcaoLambri: v })}>
                <SelectTrigger data-testid="amb-direcao"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="comprimento">No comprimento</SelectItem>
                  <SelectItem value="largura">Na largura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Terá Emenda?</Label>
              <Select value={novoAmbiente.temEmenda} onValueChange={(v) => setNovoAmbiente({ ...novoAmbiente, temEmenda: v })}>
                <SelectTrigger data-testid="amb-emenda"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nao">Não</SelectItem>
                  <SelectItem value="sim">Sim</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Formato do ambiente */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label className="text-xs">Formato do Ambiente</Label>
              <Select value={novoAmbiente.formato} onValueChange={(v) => setNovoAmbiente({ ...novoAmbiente, formato: v })}>
                <SelectTrigger data-testid="amb-formato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="retangular">Retangular</SelectItem>
                  <SelectItem value="l">Formato em L (recuo em um canto)</SelectItem>
                  <SelectItem value="u">Formato em U (recuo em uma parede)</SelectItem>
                  <SelectItem value="sacada">Retangular com sacada / varanda</SelectItem>
                  <SelectItem value="livre">Formato Livre (polígono por segmentos)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {novoAmbiente.formato === 'l' && (
              <div>
                <Label className="text-xs">Canto do Recuo</Label>
                <Select value={novoAmbiente.cantoRecuo} onValueChange={(v) => setNovoAmbiente({ ...novoAmbiente, cantoRecuo: v })}>
                  <SelectTrigger data-testid="amb-canto-recuo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUP-DIR">Superior direito</SelectItem>
                    <SelectItem value="SUP-ESQ">Superior esquerdo</SelectItem>
                    <SelectItem value="INF-DIR">Inferior direito</SelectItem>
                    <SelectItem value="INF-ESQ">Inferior esquerdo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(novoAmbiente.formato === 'u' || novoAmbiente.formato === 'sacada') && (
              <div>
                <Label className="text-xs">Parede</Label>
                <Select value={novoAmbiente.parede} onValueChange={(v) => setNovoAmbiente({ ...novoAmbiente, parede: v })}>
                  <SelectTrigger data-testid="amb-parede"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TOP">Topo (superior)</SelectItem>
                    <SelectItem value="BOTTOM">Base (inferior)</SelectItem>
                    <SelectItem value="LEFT">Esquerda</SelectItem>
                    <SelectItem value="RIGHT">Direita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {novoAmbiente.formato === 'l' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Recuo — Comprimento (m)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 2.00"
                  value={novoAmbiente.recuoComprimento}
                  onChange={(e) => setNovoAmbiente({ ...novoAmbiente, recuoComprimento: e.target.value })}
                  data-testid="amb-recuo-comp"
                />
              </div>
              <div>
                <Label className="text-xs">Recuo — Largura (m)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 1.50"
                  value={novoAmbiente.recuoLargura}
                  onChange={(e) => setNovoAmbiente({ ...novoAmbiente, recuoLargura: e.target.value })}
                  data-testid="amb-recuo-larg"
                />
              </div>
            </div>
          )}

          {(novoAmbiente.formato === 'u' || novoAmbiente.formato === 'sacada') && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">
                  {novoAmbiente.formato === 'u' ? 'Comprimento do recuo (m)' : 'Comprimento da sacada (m)'}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 2.00"
                  value={novoAmbiente.recuoComprimento}
                  onChange={(e) => setNovoAmbiente({ ...novoAmbiente, recuoComprimento: e.target.value })}
                  data-testid="amb-parede-comp"
                />
              </div>
              <div>
                <Label className="text-xs">
                  {novoAmbiente.formato === 'u' ? 'Profundidade do recuo (m)' : 'Profundidade da sacada (m)'}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 1.00"
                  value={novoAmbiente.recuoLargura}
                  onChange={(e) => setNovoAmbiente({ ...novoAmbiente, recuoLargura: e.target.value })}
                  data-testid="amb-parede-prof"
                />
              </div>
              <div>
                <Label className="text-xs">Distância do canto (m)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 2.00"
                  value={novoAmbiente.offsetParede}
                  onChange={(e) => setNovoAmbiente({ ...novoAmbiente, offsetParede: e.target.value })}
                  data-testid="amb-parede-offset"
                />
              </div>
            </div>
          )}

          {novoAmbiente.formato === 'livre' && (() => {
            const segs = novoAmbiente.segmentos || [];
            // Traça pontos em metros começando em (0,0). y-up.
            const ptsM = [[0, 0]];
            let sx = 0, sy = 0;
            for (const s of segs) {
              const c = parseFloat(s.comprimento) || 0;
              if (s.direcao === 'N') sy += c;
              else if (s.direcao === 'S') sy -= c;
              else if (s.direcao === 'E') sx += c;
              else if (s.direcao === 'O') sx -= c;
              ptsM.push([sx, sy]);
            }
            const fechou = segs.length >= 3 && Math.abs(sx) < 0.01 && Math.abs(sy) < 0.01;

            // Escala para desenho no SVG
            const SVG_W = 560, SVG_H = 300, PAD = 40;
            const xs = ptsM.map(p => p[0]);
            const ys = ptsM.map(p => p[1]);
            const minX = Math.min(0, ...xs), maxX = Math.max(0, ...xs);
            const minY = Math.min(0, ...ys), maxY = Math.max(0, ...ys);
            const bboxW = Math.max(0.5, maxX - minX);
            const bboxH = Math.max(0.5, maxY - minY);
            const escala = Math.min((SVG_W - 2 * PAD) / bboxW, (SVG_H - 2 * PAD) / bboxH);
            // Traduz (metros → pixels), SVG y invertido (para baixo)
            const toPx = ([x, y]) => [PAD + (x - minX) * escala, SVG_H - PAD - (y - minY) * escala];
            const ptsPx = ptsM.map(toPx);
            const polyPoints = ptsPx.map(p => p.join(',')).join(' ');

            const addSeg = (direcao) => {
              const c = parseFloat(livreCompAtual);
              if (!c || c <= 0) { toast.error('Digite o comprimento em metros'); return; }
              setNovoAmbiente({ ...novoAmbiente, segmentos: [...segs, { direcao, comprimento: c }] });
              setLivreCompAtual('');
            };
            const desfazer = () => {
              if (segs.length === 0) return;
              setNovoAmbiente({ ...novoAmbiente, segmentos: segs.slice(0, -1) });
            };
            const limpar = () => {
              setNovoAmbiente({ ...novoAmbiente, segmentos: [] });
              setLivreCompAtual('');
            };
            const rotacionar90 = () => {
              // Rotaciona 90° no sentido horário: N→E, E→S, S→O, O→N
              const map = { N: 'E', E: 'S', S: 'O', O: 'N' };
              const arr = segs.map(s => ({ ...s, direcao: map[s.direcao] || s.direcao }));
              setNovoAmbiente({ ...novoAmbiente, segmentos: arr });
            };
            const espelhar = () => {
              // Espelha horizontalmente: E↔O
              const map = { N: 'N', S: 'S', E: 'O', O: 'E' };
              const arr = segs.map(s => ({ ...s, direcao: map[s.direcao] || s.direcao }));
              setNovoAmbiente({ ...novoAmbiente, segmentos: arr });
            };

            return (
              <div className="space-y-3 p-3 border rounded-md bg-muted/20" data-testid="livre-sketch">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Desenhe o ambiente parede por parede</Label>
                  <div className="flex gap-1 flex-wrap">
                    <Button type="button" size="sm" variant="ghost" onClick={rotacionar90} disabled={segs.length === 0} data-testid="btn-livre-rotacionar" title="Rotacionar 90° no sentido horário">
                      ↻ 90°
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={espelhar} disabled={segs.length === 0} data-testid="btn-livre-espelhar" title="Espelhar horizontalmente">
                      ⇄ Espelhar
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={desfazer} disabled={segs.length === 0} data-testid="btn-livre-desfazer">
                      <Undo2 className="h-3.5 w-3.5 mr-1" /> Desfazer
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={limpar} disabled={segs.length === 0} data-testid="btn-livre-limpar">
                      Limpar
                    </Button>
                  </div>
                </div>

                {/* Canvas SVG mostrando o polígono sendo desenhado */}
                <div className="flex justify-center bg-white rounded border p-2">
                  <svg width="100%" height={SVG_H} viewBox={`0 0 ${SVG_W} ${SVG_H}`} preserveAspectRatio="xMidYMid meet" style={{ maxWidth: SVG_W }} data-testid="livre-svg">
                    {/* Grade suave */}
                    {Array.from({ length: Math.floor(SVG_W / 20) }).map((_, i) => (
                      <line key={`gx${i}`} x1={i * 20} y1={0} x2={i * 20} y2={SVG_H} stroke="#f1efe9" strokeWidth={0.5} />
                    ))}
                    {Array.from({ length: Math.floor(SVG_H / 20) }).map((_, i) => (
                      <line key={`gy${i}`} x1={0} y1={i * 20} x2={SVG_W} y2={i * 20} stroke="#f1efe9" strokeWidth={0.5} />
                    ))}
                    {/* Polígono */}
                    {segs.length >= 1 && (
                      <polyline
                        points={polyPoints}
                        fill={fechou ? '#FFF8E7' : 'none'}
                        stroke="#5F402B"
                        strokeWidth={1.6}
                        strokeLinejoin="round"
                      />
                    )}
                    {/* Listras do lambri (clipadas dentro do polígono quando fechado) */}
                    {fechou && (
                      <>
                        <defs>
                          <clipPath id="livre-clip">
                            <polygon points={polyPoints} />
                          </clipPath>
                        </defs>
                        {(novoAmbiente.direcaoLambri || 'comprimento') === 'largura'
                          ? Array.from({ length: 18 }).map((_, i) => {
                              const x = PAD + ((SVG_W - 2 * PAD) / 18) * (i + 0.5);
                              return <line key={`sv${i}`} x1={x} y1={0} x2={x} y2={SVG_H} stroke="#8D6E63" strokeWidth={0.5} clipPath="url(#livre-clip)" />;
                            })
                          : Array.from({ length: 18 }).map((_, i) => {
                              const y = PAD + ((SVG_H - 2 * PAD) / 18) * (i + 0.5);
                              return <line key={`sh${i}`} x1={0} y1={y} x2={SVG_W} y2={y} stroke="#8D6E63" strokeWidth={0.5} clipPath="url(#livre-clip)" />;
                            })
                        }
                        {/* Redesenha o contorno por cima */}
                        <polygon points={polyPoints} fill="none" stroke="#5F402B" strokeWidth={1.6} />
                      </>
                    )}
                    {/* Ponto inicial */}
                    <circle cx={toPx([0, 0])[0]} cy={toPx([0, 0])[1]} r={4} fill="#5F402B" />
                    {/* Ponto atual */}
                    {segs.length > 0 && (
                      <circle cx={ptsPx[ptsPx.length - 1][0]} cy={ptsPx[ptsPx.length - 1][1]} r={4} fill={fechou ? '#16a34a' : '#dc2626'} />
                    )}
                    {/* Cotas nos meios de cada segmento */}
                    {segs.map((s, i) => {
                      const p1 = ptsPx[i], p2 = ptsPx[i + 1];
                      const mx = (p1[0] + p2[0]) / 2;
                      const my = (p1[1] + p2[1]) / 2;
                      // deslocamento perpendicular para fora do centro
                      const cx = ptsPx.reduce((s, p) => s + p[0], 0) / ptsPx.length;
                      const cy = ptsPx.reduce((s, p) => s + p[1], 0) / ptsPx.length;
                      const dx = p2[0] - p1[0], dy = p2[1] - p1[1];
                      const len = Math.hypot(dx, dy) || 1;
                      let nx = -dy / len, ny = dx / len;
                      const vx = mx - cx, vy = my - cy;
                      if (nx * vx + ny * vy < 0) { nx = -nx; ny = -ny; }
                      const off = 14;
                      const compNum = parseFloat(s.comprimento) || 0;
                      const label = `${compNum.toFixed(2)}m`;
                      const tx = mx + nx * off;
                      const ty = my + ny * off;
                      return (
                        <g key={i}>
                          <rect
                            x={tx - label.length * 3.6}
                            y={ty - 8}
                            width={label.length * 7.2}
                            height={13}
                            fill="white"
                            fillOpacity="0.85"
                          />
                          <text
                            x={tx}
                            y={ty + 3}
                            fontSize={12}
                            fontWeight="bold"
                            fill="#5F402B"
                            textAnchor="middle"
                          >
                            {label}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Controles: comprimento + direção */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div>
                    <Label className="text-xs">Comprimento da próxima parede (m)</Label>
                    <Input
                      type="number" step="0.01" placeholder="Ex: 4.51"
                      value={livreCompAtual}
                      onChange={(e) => setLivreCompAtual(e.target.value)}
                      onKeyDown={(e) => {
                        // Atalhos: setas do teclado adicionam segmento
                        if (e.key === 'ArrowUp') { e.preventDefault(); addSeg('N'); }
                        else if (e.key === 'ArrowDown') { e.preventDefault(); addSeg('S'); }
                        else if (e.key === 'ArrowLeft') { e.preventDefault(); addSeg('O'); }
                        else if (e.key === 'ArrowRight') { e.preventDefault(); addSeg('E'); }
                      }}
                      data-testid="livre-comp-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-1 max-w-[160px]">
                    <div />
                    <Button type="button" variant="outline" size="sm" onClick={() => addSeg('N')} data-testid="btn-livre-n">
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <div />
                    <Button type="button" variant="outline" size="sm" onClick={() => addSeg('O')} data-testid="btn-livre-o">
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-[10px] text-center self-center text-muted-foreground">direção</div>
                    <Button type="button" variant="outline" size="sm" onClick={() => addSeg('E')} data-testid="btn-livre-e">
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                    <div />
                    <Button type="button" variant="outline" size="sm" onClick={() => addSeg('S')} data-testid="btn-livre-s">
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <div />
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {segs.length} segmento(s){segs.length > 0 ? ` · posição atual: ${sx.toFixed(2)}m X, ${sy.toFixed(2)}m Y` : ''}
                  </span>
                  {segs.length >= 3 && (
                    <span className={fechou ? 'text-green-600 font-medium' : 'text-red-600 font-medium'} data-testid="livre-status">
                      {fechou ? '✓ Polígono fechado' : '✗ Precisa fechar (voltar à origem)'}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          <Button onClick={handleAddAmbiente} className="w-full sm:w-auto" data-testid="btn-adicionar-ambiente">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar
          </Button>
        </CardContent>
      </Card>

      {/* Lista de Ambientes */}
      {ambientes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Ambientes Adicionados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ambientes.map((ambiente) => {
                const calc = calcularAmbiente(ambiente);
                // Mini preview SVG do ambiente com listras clipadas
                const pts = getPolygonPointsForAmbiente(ambiente);
                const W = 110, H = 70, P = 6;
                const xs = pts.map(p => p[0]); const ys = pts.map(p => p[1]);
                const minX = Math.min(...xs), maxX = Math.max(...xs);
                const minY = Math.min(...ys), maxY = Math.max(...ys);
                const bboxW = Math.max(0.01, maxX - minX);
                const bboxH = Math.max(0.01, maxY - minY);
                const escala = Math.min((W - 2 * P) / bboxW, (H - 2 * P) / bboxH);
                const toPx = ([x, y]) => [P + (x - minX) * escala, H - P - (y - minY) * escala];
                const polyPx = pts.map(toPx);
                const polyStr = polyPx.map(p => p.join(',')).join(' ');
                const clipId = `clip-${ambiente.id}`;
                const nStripes = 10;
                const direcao = ambiente.direcaoLambri || 'comprimento';

                return (
                  <div
                    key={ambiente.id}
                    className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg"
                  >
                    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="flex-shrink-0 bg-white rounded border">
                      <defs>
                        <clipPath id={clipId}>
                          <polygon points={polyStr} />
                        </clipPath>
                      </defs>
                      {/* Listras do lambri clipadas dentro do polígono */}
                      {direcao === 'largura'
                        ? Array.from({ length: nStripes }).map((_, i) => {
                            const x = P + ((W - 2 * P) / nStripes) * (i + 0.5);
                            return <line key={i} x1={x} y1={0} x2={x} y2={H} stroke="#8D6E63" strokeWidth={0.6} clipPath={`url(#${clipId})`} />;
                          })
                        : Array.from({ length: nStripes }).map((_, i) => {
                            const y = P + ((H - 2 * P) / nStripes) * (i + 0.5);
                            return <line key={i} x1={0} y1={y} x2={W} y2={y} stroke="#8D6E63" strokeWidth={0.6} clipPath={`url(#${clipId})`} />;
                          })
                      }
                      <polygon points={polyStr} fill="none" stroke="#5F402B" strokeWidth={1.2} />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary">{ambiente.nome}</Badge>
                        <span className="text-sm font-medium">
                          {ambiente.comprimento}m x {ambiente.largura}m
                        </span>
                        {ambiente.formato === 'l' && (
                          <Badge variant="outline" className="text-xs">
                            L · recuo {ambiente.recuoComprimento}×{ambiente.recuoLargura}m
                          </Badge>
                        )}
                        {ambiente.formato === 'u' && (
                          <Badge variant="outline" className="text-xs">
                            U · recuo {ambiente.recuoComprimento}×{ambiente.recuoLargura}m ({ambiente.parede})
                          </Badge>
                        )}
                        {ambiente.formato === 'sacada' && (
                          <Badge variant="outline" className="text-xs">
                            Sacada · {ambiente.recuoComprimento}×{ambiente.recuoLargura}m ({ambiente.parede})
                          </Badge>
                        )}
                        {ambiente.formato === 'livre' && (
                          <Badge variant="outline" className="text-xs">
                            Livre · {(ambiente.segmentos || []).length} segmentos
                          </Badge>
                        )}
                        {ambiente.comRodaTeto && (
                          <Badge variant="outline" className="text-xs">+ Roda-teto</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          Direção: {ambiente.direcaoLambri === 'largura' ? 'na largura' : 'no comprimento'}
                        </Badge>
                        {ambiente.temEmenda === 'sim' && (
                          <Badge variant="outline" className="text-xs text-red-600 border-red-300">Com emenda</Badge>
                        )}
                      </div>
                      <div className="text-sm text-primary font-semibold mt-1">
                        {calc.areaFinal.toFixed(2)}m² • {calc.quantidadeReguas} régua(s)
                        {calc.reguas && calc.reguas.length > 0 && (
                          <span className="text-muted-foreground font-normal ml-1">
                            ({calc.reguas.map(r => `${r.quantidade}×${r.comprimento.toFixed(2)}m`).join(' + ')})
                          </span>
                        )}
                        {ambiente.comRodaTeto && (
                          <span className="text-muted-foreground ml-2">
                            • {calc.perimetro.toFixed(2)}m roda-teto
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="outline" size="icon" onClick={() => handleEditAmbiente(ambiente)} data-testid={`btn-editar-ambiente-${ambiente.id}`} title="Editar ambiente">
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveAmbiente(ambiente.id)}
                        data-testid={`btn-remover-ambiente-${ambiente.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo de Réguas */}
      {totais.reguasArray.length > 0 && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-500" />
              Resumo de Réguas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {totais.reguasArray.map((regua, idx) => (
                <li key={idx} className="flex items-center justify-between p-2 bg-background rounded-lg border">
                  <span className="text-sm">
                    <span className="font-bold text-blue-500">{regua.quantidade}x</span>
                    {' '}réguas de{' '}
                    <span className="font-semibold">{regua.comprimento.toFixed(2)}m</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total de réguas:</span>
              <span className="text-xl font-bold">{totais.totalReguas} réguas</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo Roda-teto */}
      {totais.perimetroTotal > 0 && (
        <Card className="border-purple-500/30 bg-purple-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5 text-purple-500" />
              Roda-teto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total de metros lineares:</span>
              <div className="text-right">
                <span className="text-2xl font-bold text-purple-500">{totais.perimetroTotal.toFixed(2)}m</span>
                {precoRodaTeto && (
                  <p className="text-sm text-muted-foreground">
                    = R$ {totais.valorRodaTeto.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultado Final */}
      <Card className="border-green-500/30 bg-green-500/5">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Área Total</p>
              <p className="text-2xl font-bold">
                {totais.areaTotalFinal.toFixed(2)}m²
              </p>
              {comDesperdicio && (
                <p className="text-xs text-muted-foreground">
                  (com 10% desperdício)
                </p>
              )}
            </div>
            {precoM2 && (
              <div>
                <p className="text-sm text-muted-foreground">Valor Lambri</p>
                <p className="text-2xl font-bold">
                  R$ {totais.valorLambri.toFixed(2)}
                </p>
              </div>
            )}
            {precoRodaTeto && totais.perimetroTotal > 0 && (
              <div>
                <p className="text-sm text-muted-foreground">Valor Roda-teto</p>
                <p className="text-2xl font-bold text-purple-500">
                  R$ {totais.valorRodaTeto.toFixed(2)}
                </p>
              </div>
            )}
            {(precoM2 || precoRodaTeto) && (
              <div>
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className="text-2xl font-bold text-green-500">
                  R$ {totais.valorTotal.toFixed(2)}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 mt-6 flex-wrap">
            <Button variant="outline" onClick={copiarResultado} disabled={ambientes.length === 0}>
              <Copy className="h-4 w-4 mr-2" />
              Copiar Resultado
            </Button>
            <Button onClick={exportarOrcamento} disabled={ambientes.length === 0 || exportando} data-testid="btn-exportar-orcamento-lambri">
              <FileText className="h-4 w-4 mr-2" />
              {exportando ? 'Gerando PDF...' : 'Exportar Orçamento (PDF)'}
            </Button>
            <Button onClick={salvarOrcamento} disabled={ambientes.length === 0 || salvando} variant="secondary" data-testid="btn-salvar-orcamento-lambri">
              <Save className="h-4 w-4 mr-2" />
              {salvando ? 'Salvando...' : (orcamentoAtualId ? 'Atualizar' : 'Salvar Orçamento')}
            </Button>
            <Button onClick={() => setMostrarSalvos(!mostrarSalvos)} variant="outline" data-testid="btn-abrir-orcamentos-salvos">
              <FolderOpen className="h-4 w-4 mr-2" />
              Orçamentos Salvos ({orcamentosSalvos.length})
            </Button>
            {orcamentoAtualId && (
              <Button onClick={novoFormulario} variant="ghost" data-testid="btn-novo-orcamento-lambri">
                <Plus className="h-4 w-4 mr-2" />
                Novo
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Painel de Orçamentos Salvos */}
      {mostrarSalvos && (
        <Card data-testid="orcamentos-salvos-panel">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Orçamentos Salvos</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setMostrarSalvos(false)}>Fechar</Button>
            </div>
          </CardHeader>
          <CardContent>
            {orcamentosSalvos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum orçamento salvo ainda.</p>
            ) : (
              <div className="space-y-2">
                {orcamentosSalvos.map((q) => (
                  <div key={q.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border rounded-md" data-testid={`orcamento-salvo-${q.numero}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge>{q.numero}</Badge>
                        <span className="font-medium text-sm">{q.cliente_nome || '(sem cliente)'}</span>
                        <Badge variant={q.status === 'convertido' ? 'default' : 'secondary'} className="text-xs">
                          {q.status === 'convertido' ? 'Convertido em Pedido' : 'Aberto'}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(q.ambientes || []).length} ambiente(s) · Total {Number(q.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" onClick={() => baixarPdfSalvo(q.id, q.numero)} data-testid={`btn-pdf-${q.numero}`}>
                        <FileText className="h-3.5 w-3.5 mr-1" />PDF
                      </Button>
                      {q.status !== 'convertido' && (
                        <Button size="sm" variant="outline" onClick={() => carregarOrcamentoParaEditar(q)} data-testid={`btn-editar-${q.numero}`}>
                          Editar
                        </Button>
                      )}
                      {q.status !== 'convertido' && (
                        <Button size="sm" onClick={() => converterOrcamentoEmPedido(q.id)} data-testid={`btn-converter-${q.numero}`}>
                          Converter em Pedido
                        </Button>
                      )}
                      {q.status !== 'convertido' && (
                        <Button size="sm" variant="destructive" onClick={() => excluirOrcamentoSalvo(q.id)} data-testid={`btn-excluir-${q.numero}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}



      {/* Dica */}
      <div className="flex items-start gap-2 text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Cálculo:</strong><br />
          <span className="text-xs">
            • Área = Comprimento × Largura{comDesperdicio ? ' × 1.10 (10% desperdício)' : ''}<br />
            • Réguas (direção no comprimento) = Largura × 11 réguas/m — cada régua com o comprimento do ambiente<br />
            • Réguas (direção na largura) = Comprimento × 11 réguas/m — cada régua com a largura do ambiente<br />
            • Roda-teto = Perímetro (2×Comp + 2×Larg)
          </span>
        </div>
      </div>

      {/* Modal cadastro rápido de cliente */}
      <Dialog open={showNovoClienteModal} onOpenChange={setShowNovoClienteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cadastrar Cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={novoClienteForm.tipo === 'PF' ? 'default' : 'outline'} onClick={() => setNovoClienteForm({ ...novoClienteForm, tipo: 'PF' })}>Pessoa Física</Button>
              <Button type="button" size="sm" variant={novoClienteForm.tipo === 'PJ' ? 'default' : 'outline'} onClick={() => setNovoClienteForm({ ...novoClienteForm, tipo: 'PJ' })}>Pessoa Jurídica</Button>
            </div>
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={novoClienteForm.nome} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, nome: e.target.value })} data-testid="novo-cli-nome" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Telefone</Label>
                <Input value={novoClienteForm.telefone} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, telefone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{novoClienteForm.tipo === 'PJ' ? 'CNPJ' : 'CPF'}</Label>
                <Input value={novoClienteForm.cpf_cnpj} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, cpf_cnpj: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Endereço</Label>
              <Input value={novoClienteForm.endereco} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, endereco: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Bairro</Label>
                <Input value={novoClienteForm.bairro} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, bairro: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Cidade</Label>
                <Input value={novoClienteForm.cidade} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, cidade: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">UF</Label>
                <Input value={novoClienteForm.estado} onChange={(e) => setNovoClienteForm({ ...novoClienteForm, estado: e.target.value.toUpperCase().slice(0, 2) })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowNovoClienteModal(false)}>Cancelar</Button>
              <Button onClick={cadastrarNovoCliente} disabled={savingCliente} data-testid="btn-salvar-novo-cliente">
                {savingCliente ? 'Salvando...' : 'Cadastrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

// ============ CALCULADORA DE ROMANEIO DE MADEIRA ============

// Função para formatar valor como moeda brasileira (R$ 1.000,00)
const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return '';
  return valor.toLocaleString('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Função para converter string formatada em número
const parseMoeda = (valorStr) => {
  if (!valorStr) return 0;
  // Remove "R$", pontos de milhar e substitui vírgula por ponto
  const limpo = valorStr
    .replace(/[R$\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  return parseFloat(limpo) || 0;
};

// Função para formatar input de moeda enquanto digita
const formatarInputMoeda = (valor) => {
  // Remove tudo exceto números
  let numeros = valor.replace(/\D/g, '');
  
  if (!numeros) return '';
  
  // Converte para número (centavos)
  let valorNumerico = parseInt(numeros, 10);
  
  // Divide por 100 para ter os centavos
  let valorDecimal = valorNumerico / 100;
  
  // Formata como moeda brasileira
  return valorDecimal.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RomaneioCalculator = memo(function RomaneioCalculator() {
  const [tabelas, setTabelas] = useState([]);
  const [tiposMadeira, setTiposMadeira] = useState([]);
  const [romaneiosSalvos, setRomaneiosSalvos] = useState([]);
  const [romaneioAtual, setRomaneioAtual] = useState(null); // Romaneio sendo editado
  const [tituloRomaneio, setTituloRomaneio] = useState('');
  const [showRomaneiosSalvos, setShowRomaneiosSalvos] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  
  const [novaTabela, setNovaTabela] = useState({
    madeira: '',
    espessura: '',
    precoM3: '',        // valor numérico real
    precoM3Display: ''  // valor formatado para exibição
  });

  // Carregar tipos de madeira cadastrados
  useEffect(() => {
    const fetchTiposMadeira = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tipos-madeira`);
        const data = await res.json();
        setTiposMadeira(data);
      } catch (error) {
        console.error('Erro ao carregar tipos de madeira:', error);
      }
    };
    fetchTiposMadeira();
  }, []);

  // Carregar romaneios salvos
  useEffect(() => {
    const fetchRomaneios = async () => {
      try {
        const res = await fetch(`${API_URL}/api/romaneios`);
        const data = await res.json();
        setRomaneiosSalvos(data);
      } catch (error) {
        console.error('Erro ao carregar romaneios:', error);
      }
    };
    fetchRomaneios();
  }, []);

  // Handler para selecionar tipo de madeira do dropdown
  const handleSelectMadeira = useCallback((tipoId) => {
    const tipo = tiposMadeira.find(t => t.id === tipoId);
    if (tipo) {
      setNovaTabela(prev => ({
        ...prev,
        madeira: tipo.nome,
        precoM3: tipo.preco_m3.toString(),
        precoM3Display: formatarMoeda(tipo.preco_m3)
      }));
    }
  }, [tiposMadeira]);

  // Handler para input de preço com máscara de moeda
  const handlePrecoChange = useCallback((e) => {
    const formatted = formatarInputMoeda(e.target.value);
    const valorNumerico = parseMoeda(formatted);
    setNovaTabela(prev => ({ 
      ...prev, 
      precoM3: valorNumerico.toString(),
      precoM3Display: formatted
    }));
  }, []);

  // Salvar romaneio
  const handleSalvarRomaneio = useCallback(async () => {
    if (tabelas.length === 0) {
      toast.error('Adicione pelo menos uma tabela antes de salvar');
      return;
    }
    if (!tituloRomaneio.trim()) {
      toast.error('Informe um título para o romaneio');
      return;
    }

    setLoadingSave(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        titulo: tituloRomaneio,
        tabelas: tabelas.map(t => ({
          id: t.id.toString(),
          nome: t.nome,
          espessura: t.espessura,
          precoM3: t.precoM3,
          pecas: t.pecas.map(p => ({
            id: p.id.toString(),
            comprimento: p.comprimento,
            comprimentoMetros: p.comprimentoMetros,
            largura: p.largura,
            larguraDisplay: p.larguraDisplay,
            quantidade: p.quantidade || '1'
          }))
        }))
      };

      if (romaneioAtual) {
        // Atualizar existente
        await fetch(`${API_URL}/api/romaneios/${romaneioAtual.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        toast.success('Romaneio atualizado!');
      } else {
        // Criar novo
        const res = await fetch(`${API_URL}/api/romaneios`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        const novoRomaneio = await res.json();
        setRomaneioAtual(novoRomaneio);
        toast.success('Romaneio salvo!');
      }

      // Recarregar lista
      const res = await fetch(`${API_URL}/api/romaneios`);
      const data = await res.json();
      setRomaneiosSalvos(data);
    } catch (error) {
      toast.error('Erro ao salvar romaneio');
      console.error(error);
    } finally {
      setLoadingSave(false);
    }
  }, [tabelas, tituloRomaneio, romaneioAtual]);

  // Carregar romaneio salvo
  const handleCarregarRomaneio = useCallback((romaneio) => {
    setRomaneioAtual(romaneio);
    setTituloRomaneio(romaneio.titulo);
    setTabelas(romaneio.tabelas.map(t => ({
      ...t,
      id: parseInt(t.id) || Date.now(),
      pecas: t.pecas.map(p => ({
        ...p,
        id: parseInt(p.id) || Date.now()
      }))
    })));
    setShowRomaneiosSalvos(false);
    toast.success(`Romaneio "${romaneio.titulo}" carregado`);
  }, []);

  // Novo romaneio
  const handleNovoRomaneio = useCallback(() => {
    setRomaneioAtual(null);
    setTituloRomaneio('');
    setTabelas([]);
    toast.info('Novo romaneio iniciado');
  }, []);

  // Excluir romaneio
  const handleExcluirRomaneio = useCallback(async (romaneioId) => {
    if (!window.confirm('Tem certeza que deseja excluir este romaneio?')) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/romaneios/${romaneioId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Romaneio excluído');
      
      // Recarregar lista
      const res = await fetch(`${API_URL}/api/romaneios`);
      const data = await res.json();
      setRomaneiosSalvos(data);

      // Se era o romaneio atual, limpar
      if (romaneioAtual?.id === romaneioId) {
        handleNovoRomaneio();
      }
    } catch (error) {
      toast.error('Erro ao excluir romaneio');
    }
  }, [romaneioAtual, handleNovoRomaneio]);

  // Adicionar nova tabela/lote
  const handleAddTabela = useCallback(() => {
    if (!novaTabela.espessura) {
      toast.error('Informe a espessura padrão');
      return;
    }
    if (!novaTabela.madeira) {
      toast.error('Informe o tipo de madeira');
      return;
    }
    
    setTabelas(prev => [...prev, {
      id: Date.now(),
      nome: novaTabela.madeira,
      espessura: novaTabela.espessura,
      precoM3: novaTabela.precoM3,
      pecas: []
    }]);
    setNovaTabela({ madeira: '', espessura: '', precoM3: '', precoM3Display: '' });
    toast.success('Tabela criada!');
  }, [novaTabela]);

  // Remover tabela
  const handleRemoveTabela = useCallback((tabelaId) => {
    setTabelas(prev => prev.filter(t => t.id !== tabelaId));
  }, []);

  // Adicionar peça a uma tabela
  const handleAddPeca = useCallback((tabelaId, peca) => {
    if (!peca.comprimento || !peca.largura) {
      toast.error('Informe comprimento e largura');
      return false;
    }
    
    setTabelas(prev => prev.map(t => {
      if (t.id === tabelaId) {
        return {
          ...t,
          pecas: [...t.pecas, { ...peca, id: Date.now() }]
        };
      }
      return t;
    }));
    return true;
  }, []);

  // Remover peça de uma tabela
  const handleRemovePeca = useCallback((tabelaId, pecaId) => {
    setTabelas(prev => prev.map(t => {
      if (t.id === tabelaId) {
        return {
          ...t,
          pecas: t.pecas.filter(p => p.id !== pecaId)
        };
      }
      return t;
    }));
  }, []);

  // Calcular volume de uma peça em m³
  const calcularVolumePeca = useCallback((peca, espessura) => {
    const comp = parseFloat(peca.comprimento) || 0;
    const larg = parseFloat(peca.largura) || 0;
    const esp = parseFloat(espessura) || 0;
    const qtd = parseInt(peca.quantidade) || 1;
    
    // cm³ para m³: dividir por 1.000.000
    const volumeUnitario = (comp * larg * esp) / 1000000;
    const volumeTotal = volumeUnitario * qtd;
    
    return { volumeUnitario, volumeTotal, qtd };
  }, []);

  // Calcular totais de uma tabela
  const calcularTotaisTabela = useCallback((tabela) => {
    let volumeTotal = 0;
    let totalPecas = 0;
    
    tabela.pecas.forEach(peca => {
      const calc = calcularVolumePeca(peca, tabela.espessura);
      volumeTotal += calc.volumeTotal;
      totalPecas += calc.qtd;
    });
    
    return { volumeTotal, totalPecas };
  }, [calcularVolumePeca]);

  // Totais gerais
  const totaisGerais = useMemo(() => {
    let volumeTotal = 0;
    let totalPecas = 0;
    let valorTotal = 0;
    
    tabelas.forEach(tabela => {
      const calc = calcularTotaisTabela(tabela);
      volumeTotal += calc.volumeTotal;
      totalPecas += calc.totalPecas;
      const precoTabela = parseFloat(tabela.precoM3) || 0;
      valorTotal += calc.volumeTotal * precoTabela;
    });
    
    return { volumeTotal, totalPecas, valorTotal };
  }, [tabelas, calcularTotaisTabela]);

  const copiarResultado = useCallback(() => {
    let texto = 'ROMANEIO DE MADEIRA\n';
    texto += '='.repeat(40) + '\n\n';
    
    tabelas.forEach(tabela => {
      const totais = calcularTotaisTabela(tabela);
      const precoTabela = parseFloat(tabela.precoM3) || 0;
      const valorTabela = totais.volumeTotal * precoTabela;
      
      texto += `${tabela.nome.toUpperCase()} (Espessura: ${tabela.espessura}cm) - R$ ${precoTabela.toFixed(2)}/m³\n`;
      texto += '-'.repeat(30) + '\n';
      
      tabela.pecas.forEach(peca => {
        const calc = calcularVolumePeca(peca, tabela.espessura);
        const compMetros = peca.comprimentoMetros || (parseFloat(peca.comprimento) / 100).toFixed(2).replace('.', ',');
        const largDisplay = peca.larguraDisplay || (parseFloat(peca.largura) / 100).toFixed(2).replace('.', ',');
        texto += `• ${peca.quantidade || 1}x ${compMetros}m × ${largDisplay}cm × ${tabela.espessura}cm = ${calc.volumeTotal.toFixed(4)}m³\n`;
      });
      
      texto += `Subtotal: ${totais.totalPecas} peças = ${totais.volumeTotal.toFixed(4)}m³ = ${formatarMoeda(valorTabela)}\n\n`;
    });
    
    texto += '='.repeat(40) + '\n';
    texto += `TOTAL GERAL: ${totaisGerais.totalPecas} peças = ${totaisGerais.volumeTotal.toFixed(4)}m³\n`;
    texto += `Valor Total: ${formatarMoeda(totaisGerais.valorTotal)}`;
    
    navigator.clipboard.writeText(texto);
    toast.success('Romaneio copiado!');
  }, [tabelas, totaisGerais, calcularTotaisTabela, calcularVolumePeca]);

  const imprimirRomaneio = useCallback(() => {
    const dataAtual = new Date().toLocaleDateString('pt-BR');
    
    let conteudo = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Romaneio de Madeira</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 10px; 
            font-size: 9px;
            color: #000;
          }
          .titulo-principal {
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #000;
          }
          .tabela-romaneio {
            margin-bottom: 15px;
            page-break-inside: avoid;
          }
          .header-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 5px;
          }
          .header-table td {
            border: 1px solid #000;
            padding: 3px 6px;
            font-size: 10px;
          }
          .header-table .label {
            font-weight: bold;
            background: #f0f0f0;
          }
          .data-container {
            display: flex;
            gap: 3px;
          }
          .coluna {
            flex: 1;
          }
          .coluna table {
            width: 100%;
            border-collapse: collapse;
          }
          .coluna th {
            border: 1px solid #000;
            padding: 2px 3px;
            font-size: 8px;
            font-weight: bold;
            background: #f0f0f0;
            text-align: center;
          }
          .coluna td {
            border: 1px solid #000;
            padding: 1px 3px;
            font-size: 8px;
            text-align: center;
          }
          .separador {
            border-top: 2px solid #000;
            margin: 15px 0;
          }
          @media print {
            body { padding: 5px; }
            .tabela-romaneio { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="titulo-principal">Romaneio de Madeira</div>
    `;

    // Para cada tabela, gerar um bloco separado
    tabelas.forEach((tabela, tabelaIndex) => {
      const totaisTabela = calcularTotaisTabela(tabela);
      const precoTabela = parseFloat(tabela.precoM3) || 0;
      const valorTabela = precoTabela * totaisTabela.volumeTotal;
      
      // Expandir peças pela quantidade
      const pecasExpandidas = [];
      tabela.pecas.forEach(peca => {
        const calc = calcularVolumePeca(peca, tabela.espessura);
        const compMetros = peca.comprimentoMetros || (parseFloat(peca.comprimento) / 100).toFixed(2).replace('.', ',');
        const largDisplay = peca.larguraDisplay || (parseFloat(peca.largura) / 100).toFixed(2).replace('.', ',');
        
        for (let i = 0; i < calc.qtd; i++) {
          pecasExpandidas.push({
            c: compMetros,
            l: largDisplay,
            vm3: calc.volumeUnitario.toFixed(4).replace('.', ',')
          });
        }
      });

      // Calcular linhas por coluna (preencher verticalmente)
      const numColunas = 4;
      const linhasPorColuna = Math.ceil(pecasExpandidas.length / numColunas);
      
      // Dividir em 4 colunas (vertical primeiro)
      const colunas = [[], [], [], []];
      pecasExpandidas.forEach((peca, index) => {
        const colunaIndex = Math.floor(index / linhasPorColuna);
        if (colunaIndex < numColunas) {
          colunas[colunaIndex].push(peca);
        } else {
          colunas[numColunas - 1].push(peca);
        }
      });

      // Encontrar o máximo de linhas
      const maxLinhas = Math.max(...colunas.map(c => c.length));

      conteudo += `
        <div class="tabela-romaneio">
          <table class="header-table">
            <tr>
              <td class="label">MADEIRA</td>
              <td colspan="2">${tabela.nome.toUpperCase()}</td>
              <td class="label">TOTAL M³</td>
              <td>${totaisTabela.volumeTotal.toFixed(4).replace('.', ',')} m³</td>
              <td class="label">DATA</td>
              <td>${dataAtual}</td>
            </tr>
            <tr>
              <td class="label">VALOR M³</td>
              <td colspan="2">${formatarMoeda(precoTabela)}</td>
              <td class="label">TOTAL R$</td>
              <td colspan="3">${formatarMoeda(valorTabela)}</td>
            </tr>
            <tr>
              <td class="label">BITOLA</td>
              <td colspan="6">${tabela.espessura}</td>
            </tr>
          </table>

          <div class="data-container">
      `;

      // Gerar as 4 colunas
      for (let i = 0; i < numColunas; i++) {
        conteudo += `
          <div class="coluna">
            <table>
              <thead>
                <tr>
                  <th>C</th>
                  <th>L</th>
                  <th>VM³</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        colunas[i].forEach(peca => {
          conteudo += `
            <tr>
              <td>${peca.c}</td>
              <td>${peca.l}</td>
              <td>${peca.vm3}</td>
            </tr>
          `;
        });

        // Preencher linhas vazias para manter alinhamento
        for (let j = colunas[i].length; j < maxLinhas; j++) {
          conteudo += `<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>`;
        }
        
        conteudo += `
              </tbody>
            </table>
          </div>
        `;
      }

      conteudo += `
          </div>
        </div>
      `;

      // Adicionar separador entre tabelas (exceto na última)
      if (tabelaIndex < tabelas.length - 1) {
        conteudo += `<div class="separador"></div>`;
      }
    });

    // Total geral se houver mais de uma tabela
    if (tabelas.length > 1) {
      conteudo += `
        <div class="separador"></div>
        <table class="header-table" style="background: #f0f0f0;">
          <tr>
            <td class="label" style="font-size: 12px;">TOTAL GERAL</td>
            <td style="font-size: 12px;">${totaisGerais.totalPecas} peças</td>
            <td style="font-size: 12px;">${totaisGerais.volumeTotal.toFixed(4).replace('.', ',')} m³</td>
            <td style="font-size: 12px;">${formatarMoeda(totaisGerais.valorTotal)}</td>
          </tr>
        </table>
      `;
    }

    conteudo += `
      </body>
      </html>
    `;

    const janelaImpressao = window.open('', '_blank');
    janelaImpressao.document.write(conteudo);
    janelaImpressao.document.close();
    janelaImpressao.focus();
    setTimeout(() => {
      janelaImpressao.print();
    }, 250);
  }, [tabelas, totaisGerais, calcularTotaisTabela, calcularVolumePeca]);

  return (
    <div className="space-y-4">
      {/* Header com ações do Romaneio */}
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex-1 flex items-center gap-3">
              <Input
                placeholder="Título do Romaneio (ex: Cliente João - Obra Centro)"
                value={tituloRomaneio}
                onChange={(e) => setTituloRomaneio(e.target.value)}
                className="max-w-md"
              />
              {romaneioAtual && (
                <Badge variant="secondary" className="whitespace-nowrap">
                  Editando: {romaneioAtual.titulo}
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowRomaneiosSalvos(!showRomaneiosSalvos)}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Salvos ({romaneiosSalvos.length})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleNovoRomaneio}
              >
                <FileText className="h-4 w-4 mr-2" />
                Novo
              </Button>
              <Button 
                size="sm"
                onClick={handleSalvarRomaneio}
                disabled={loadingSave || tabelas.length === 0}
              >
                <Save className="h-4 w-4 mr-2" />
                {romaneioAtual ? 'Atualizar' : 'Salvar'}
              </Button>
            </div>
          </div>

          {/* Lista de Romaneios Salvos */}
          {showRomaneiosSalvos && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2">Romaneios Salvos</h4>
              {romaneiosSalvos.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum romaneio salvo</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {romaneiosSalvos.map(rom => (
                    <div 
                      key={rom.id}
                      className="flex items-center justify-between p-2 bg-background rounded border hover:border-primary/50 cursor-pointer"
                      onClick={() => handleCarregarRomaneio(rom)}
                    >
                      <div>
                        <p className="font-medium text-sm">{rom.titulo}</p>
                        <p className="text-xs text-muted-foreground">
                          {rom.totalPecas} peças • {rom.volumeTotal.toFixed(4)}m³ • {formatarMoeda(rom.valorTotal)}
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExcluirRomaneio(rom.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Criar Nova Tabela */}
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Nova Tabela / Lote
          </CardTitle>
          <CardDescription>
            Selecione o tipo de madeira ou digite manualmente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Madeira</Label>
              {tiposMadeira.length > 0 ? (
                <Select onValueChange={handleSelectMadeira}>
                  <SelectTrigger>
                    <SelectValue placeholder={novaTabela.madeira || "Selecione..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposMadeira.map(tipo => (
                      <SelectItem key={tipo.id} value={tipo.id}>
                        {tipo.nome} - {formatarMoeda(tipo.preco_m3)}/m³
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Ex: Cedrinho"
                  value={novaTabela.madeira}
                  onChange={(e) => setNovaTabela({ ...novaTabela, madeira: e.target.value })}
                />
              )}
            </div>
            <div>
              <Label>Espessura (cm)</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="Ex: 2.5"
                value={novaTabela.espessura}
                onChange={(e) => setNovaTabela({ ...novaTabela, espessura: e.target.value })}
              />
            </div>
            <div>
              <Label>Valor m³ (R$)</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={novaTabela.precoM3Display}
                onChange={handlePrecoChange}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAddTabela} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Criar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabelas de Romaneio */}
      {tabelas.map((tabela) => (
        <TabelaRomaneio
          key={tabela.id}
          tabela={tabela}
          onAddPeca={handleAddPeca}
          onRemovePeca={handleRemovePeca}
          onRemoveTabela={handleRemoveTabela}
          calcularVolumePeca={calcularVolumePeca}
          calcularTotaisTabela={calcularTotaisTabela}
        />
      ))}

      {/* Resultado Final */}
      {tabelas.length > 0 && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5 text-green-500" />
              Total Geral do Romaneio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Total de Peças</p>
                <p className="text-2xl font-bold">{totaisGerais.totalPecas}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Volume Total</p>
                <p className="text-2xl font-bold text-green-500">
                  {totaisGerais.volumeTotal.toFixed(4)}m³
                </p>
              </div>
              {totaisGerais.valorTotal > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatarMoeda(totaisGerais.valorTotal)}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={copiarResultado}>
                <Copy className="h-4 w-4 mr-2" />
                Copiar Romaneio
              </Button>
              <Button variant="outline" onClick={imprimirRomaneio}>
                <Printer className="h-4 w-4 mr-2" />
                Imprimir
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dica */}
      <div className="flex items-start gap-2 text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Cálculo:</strong><br />
          <span className="text-xs">
            • Volume = (Comprimento × Largura × Espessura) ÷ 1.000.000<br />
            • Medidas em centímetros, resultado em m³
          </span>
        </div>
      </div>
    </div>
  );
});

// Componente de Tabela Individual de Romaneio
const TabelaRomaneio = memo(function TabelaRomaneio({
  tabela,
  onAddPeca,
  onRemovePeca,
  onRemoveTabela,
  calcularVolumePeca,
  calcularTotaisTabela
}) {
  const [novaPeca, setNovaPeca] = useState({
    comprimento: '',
    comprimentoDisplay: '',
    largura: '',
    larguraDisplay: '',
    quantidade: ''
  });

  // Ref para o input de comprimento (para focar após adicionar)
  const comprimentoInputRef = useRef(null);

  // Formatar valor com vírgula automática (ex: 350 -> 3,50)
  const formatarComVirgula = (rawValue) => {
    if (rawValue === '') return { valor: '', display: '' };
    
    const numValue = parseInt(rawValue, 10);
    const valorDecimal = numValue / 100;
    const displayValue = valorDecimal.toFixed(2).replace('.', ',');
    const valorCm = valorDecimal * 100;
    
    return { valor: valorCm.toString(), display: displayValue };
  };

  // Formatar comprimento em metros com vírgula
  const handleComprimentoChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const { valor, display } = formatarComVirgula(rawValue);
    setNovaPeca({ ...novaPeca, comprimento: valor, comprimentoDisplay: display });
  };

  // Formatar largura em centímetros com vírgula
  const handleLarguraChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const { valor, display } = formatarComVirgula(rawValue);
    setNovaPeca({ ...novaPeca, largura: valor, larguraDisplay: display });
  };

  const handleAdd = () => {
    const success = onAddPeca(tabela.id, {
      comprimento: novaPeca.comprimento,
      comprimentoMetros: novaPeca.comprimentoDisplay,
      largura: novaPeca.largura,
      larguraDisplay: novaPeca.larguraDisplay,
      quantidade: novaPeca.quantidade
    });
    if (success) {
      setNovaPeca({ comprimento: '', comprimentoDisplay: '', largura: '', larguraDisplay: '', quantidade: '' });
      toast.success('Peça adicionada!');
      // Focar no input de comprimento para próxima peça
      setTimeout(() => {
        comprimentoInputRef.current?.focus();
      }, 100);
    }
  };

  // Adicionar ao pressionar Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const totais = calcularTotaisTabela(tabela);
  const precoTabela = parseFloat(tabela.precoM3) || 0;
  const valorTabela = totais.volumeTotal * precoTabela;

  return (
    <Card className="border-amber-500/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-500" />
              {tabela.nome}
            </CardTitle>
            <CardDescription>
              Espessura: <strong>{tabela.espessura}cm</strong> | Valor: <strong>{formatarMoeda(precoTabela)}/m³</strong>
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => onRemoveTabela(tabela.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Adicionar Peça */}
        <div className="grid grid-cols-4 gap-2">
          <div>
            <Label className="text-xs">Comprimento (m)</Label>
            <Input
              ref={comprimentoInputRef}
              type="text"
              inputMode="numeric"
              placeholder="0,00"
              value={novaPeca.comprimentoDisplay}
              onChange={handleComprimentoChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div>
            <Label className="text-xs">Largura (cm)</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="0,00"
              value={novaPeca.larguraDisplay}
              onChange={handleLarguraChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div>
            <Label className="text-xs">Qtd</Label>
            <Input
              type="number"
              placeholder="1"
              value={novaPeca.quantidade}
              onChange={(e) => setNovaPeca({ ...novaPeca, quantidade: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="flex items-end">
            <Button onClick={handleAdd} size="sm" className="w-full">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Lista de Peças */}
        {tabela.pecas.length > 0 && (
          <div className="space-y-1">
            {tabela.pecas.map((peca) => {
              const calc = calcularVolumePeca(peca, tabela.espessura);
              const compMetros = peca.comprimentoMetros || (parseFloat(peca.comprimento) / 100).toFixed(2).replace('.', ',');
              const largDisplay = peca.larguraDisplay || (parseFloat(peca.largura) / 100).toFixed(2).replace('.', ',');
              return (
                <div
                  key={peca.id}
                  className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm"
                >
                  <span>
                    <span className="font-semibold text-amber-500">{calc.qtd}x</span>
                    {' '}{compMetros}m × {largDisplay}cm × {tabela.espessura}cm
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs">
                      {calc.volumeTotal.toFixed(4)}m³
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => onRemovePeca(tabela.id, peca.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Subtotal da Tabela */}
        {tabela.pecas.length > 0 && (
          <div className="pt-2 border-t flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              Subtotal: {totais.totalPecas} peças
            </span>
            <div className="text-right">
              <span className="font-bold text-amber-500">
                {totais.volumeTotal.toFixed(4)}m³
              </span>
              {precoTabela > 0 && (
                <span className="ml-2 text-sm font-semibold">
                  = {formatarMoeda(valorTabela)}
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// ============ COMPONENTE PRINCIPAL ============
const CalculatorsTab = memo(function CalculatorsTab() {
  const [activeCalc, setActiveCalc] = useState('alizar');

  return (
    <div className="space-y-4">
      {/* Header com estatísticas */}
      <div className="grid grid-cols-3 gap-3">
        <Card 
          className={`cursor-pointer transition-all ${activeCalc === 'alizar' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`}
          onClick={() => setActiveCalc('alizar')}
        >
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-orange-500/10">
              <DoorOpen className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <p className="font-semibold">Alizares</p>
              <p className="text-xs text-muted-foreground">Portas e Janelas</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${activeCalc === 'lambri' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`}
          onClick={() => setActiveCalc('lambri')}
        >
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <LayoutGrid className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="font-semibold">Forro Lambri</p>
              <p className="text-xs text-muted-foreground">Área em m²</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeCalc === 'romaneio' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`}
          onClick={() => setActiveCalc('romaneio')}
        >
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-500/10">
              <Package className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <p className="font-semibold">Romaneio</p>
              <p className="text-xs text-muted-foreground">Madeira em m³</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calculadora Ativa */}
      {activeCalc === 'alizar' && <AlizarCalculator />}
      {activeCalc === 'lambri' && <LambriCalculator />}
      {activeCalc === 'romaneio' && <RomaneioCalculator />}
    </div>
  );
});

export default CalculatorsTab;
